import * as vp from 'vopay';
import {
  Box,
  withStyles,
  Table,
  formatDate,
  capitalizeFirstLetter,
  Tooltip,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
} from 'vopay';
import type { SubscriptionItem } from 'vopay';

import { FunctionComponent, useEffect, useRef, useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './ReportsPage.module.scss';

import usePostDownloadReport from '@/hooks/useApi/usePostDownloadReport';
import useFileDownload from '@/hooks/useFiledDownload';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import usePostEditReport from '@/hooks/useApi/usePostEditReport';
import { useToast } from '@/providers/Toast.provider';
import { useDataFetchIndicator } from '@/hooks';

import { Button, Chip, TransactionStatus, TransactionTypeLabel } from 'UI';

const classes = withStyles(styles);

type FC = FunctionComponent<ReportsTableProps>;

type ReportsTableProps = {
  reports: SubscriptionItem[];
  activeTab: string;
  onUpdate?: (e: boolean) => void;
};

type Column = {
  ID: string;
  reportName: string;
  availableOn: string;
  description: string;
  paymentRails: {
    [key: string]: string;
  };
  reportType: string;
  recipientEmails: string;
  reportAvailable: boolean;
  frequency?: string;
  isActive?: boolean;
  reportID?: string;
};

const ReportsTable: FC = ({ reports, activeTab, onUpdate }: ReportsTableProps) => {
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.ReportTable' });
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { downloadXlsxFileType } = useFileDownload();
  const [downloadReport, postDownloadReport] = usePostDownloadReport({});
  const [, , , postEditSubscriptionStatus] = usePostEditReport({});
  const alertDialog = useAlertDialog();
  const toast = useToast();

  const subscriptionColumns: vp.ColConfig<Column, string>[] = [
    {
      id: 'Frequency',
      header: t('Columns.frequency'),
      align: 'left',
      field: 'frequency',
      style: {
        whiteSpace: 'nowrap',
      },
    },
    {
      id: 'SubscriptionStatus',
      header: t('Columns.subscriptionStatus'),
      align: 'left',
      template: (row) => (
        <Box ai="center" gap="sm">
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(row.data.isActive ? 'active' : 'inactive')}
              label={capitalizeFirstLetterOfWords(
                row.data.isActive ? t('ActionStatus.active') : t('ActionStatus.active'),
              )}
            />
          </Box>
        </Box>
      ),
      style: {
        whiteSpace: 'nowrap',
      },
    },
    {
      id: 'StatusButton',
      header: '',
      align: 'right',
      template: (row) => (
        <>
          {row.data.isActive ? (
            <Button
              id={`deactivate-button-${row.data.ID}`}
              size="small"
              onClick={async () => {
                try {
                  const result = await alertDialog.caution(
                    t('DialogueMessage.deactivateSubscription'),
                    <Box gap="sm">
                      {t('DialogueMessage.deactivateSubscriptionDescription', {
                        reportName: row.data.reportName,
                        recipientEmails: row.data.recipientEmails.split(',')[0],
                      })}
                    </Box>,
                    {
                      abortLabel: t('ActionStatus.cancel'),
                      confirmLabel: t('ActionStatus.deactivate'),
                    },
                  );
                  if (result === 'confirm') {
                    setDataFetchingInfo(true);
                    const res = await postEditSubscriptionStatus({
                      PortalUser: response.LoginUserName || '',
                      SubscriptionID: row.data.ID || '',
                      IsActive: row.data.isActive ? '0' : '1',
                    });

                    if (res.Success) {
                      toast.success(<strong>{t('ToastMessage.reportDeactivated')}</strong>);
                      onUpdate?.(true);
                    }
                  }
                } catch (err) {
                  toast.neutral(
                    t('ToastMessage.deactivateFail', {
                      label: err,
                    }),
                  );
                } finally {
                  setDataFetchingInfo(false);
                }
              }}
              style={{ width: '100px' }}
            >
              {t('ActionStatus.deactivate')}
            </Button>
          ) : (
            <Button
              id={`activate-button-${row.data.ID}`}
              size="small"
              onClick={async () => {
                try {
                  setDataFetchingInfo(true);
                  const res = await postEditSubscriptionStatus({
                    PortalUser: response.LoginUserName || '',
                    SubscriptionID: row.data.ID || '',
                    IsActive: row.data.isActive ? '0' : '1',
                  });
                  if (res.Success) {
                    toast.success(<strong>{t('ToastMessage.reportActivated')}</strong>);

                    onUpdate?.(true);
                  } else {
                    await alertDialog.caution(
                      t('DialogueMessage.activateFail', {
                        label: row.data.reportName,
                      }),
                      `${res.ErrorMessage}`,
                      {
                        confirmLabel: t('ActionStatus.okay'),
                      },
                    );
                  }
                } catch (err) {
                  toast.neutral(
                    t('ToastMessage.activateFail', {
                      label: err,
                    }),
                  );
                } finally {
                  setDataFetchingInfo(false);
                }
              }}
              style={{ width: '100px' }}
            >
              {t('ActionStatus.activate')}
            </Button>
          )}
        </>
      ),
    },
  ];

  const combinedColumns: vp.ColConfig<Column, string>[] = [
    {
      id: 'ReportName',
      header: t('Columns.reportName'),
      align: 'left',
      template: (row) => (
        <Link className={styles.reportNameLink} to={`/reports/${vp.removeWhiteSpaceURI(row.data.ID || '')}`}>
          {row.data.reportName}
        </Link>
      ),
      style: {
        whiteSpace: 'nowrap',
      },
    },
    {
      id: 'ReportType',
      header: t('Columns.reportType'),
      align: 'left',
      field: 'reportType',
    },
    {
      id: 'Description',
      header: t('Columns.description'),
      align: 'left',
      field: 'description',
    },
    ...(activeTab === 'subscription' ? [subscriptionColumns[0]] : []),
    {
      id: 'PaymentRails',
      header: t('Columns.paymentRails'),
      align: 'left',
      template: (row) =>
        Object.values(row.data.paymentRails).length !== 0 ? (
          <Labels labels={Object.values(row.data.paymentRails)} />
        ) : (
          <div>{t('notApplicable')}</div>
        ),
    },
    {
      id: 'AvailableOn',
      header: t('Columns.availableOn'),
      align: 'left',
      field: 'availableOn',
      style: {
        whiteSpace: 'nowrap',
      },
    },
    ...(activeTab === 'subscription' ? [subscriptionColumns[1]] : []),
    ...(activeTab === 'subscription'
      ? []
      : [
          {
            id: 'DownloadButton',
            header: '',
            align: 'right' as const,
            template: (row: { data: Column }) =>
              row.data.reportAvailable && (
                <Button
                  id={`download-button-${row.data.description}`}
                  size="small"
                  onClick={() => {
                    postDownloadReport({
                      ReportID: row.data.reportID || '',
                    });
                  }}
                >
                  {t('ActionStatus.download')}
                </Button>
              ),
          },
        ]),
    ...(activeTab === 'subscription' ? [subscriptionColumns[2]] : []),
  ];

  useEffect(() => {
    const { Success, ReportFile, ReportName } = downloadReport;

    if (Success) {
      downloadXlsxFileType(ReportFile, ReportName);
    }
  }, [downloadReport]);

  return (
    <>
      <Box className={classes('oneTimeReportsTableContainer', 'scrollbar')}>
        <Table
          zebra
          id="reports"
          version="2"
          rows={reports.map((value) => {
            return {
              id: value.ID,
              data: {
                reportName: value.ReportName,
                availableOn: formatDate(value.ScheduledDate),
                description: value.Description,
                paymentRails: value.PaymentRails,
                ...(activeTab === 'subscription' && { frequency: capitalizeFirstLetter(value.Frequency || '') }),
                ...(activeTab === 'subscription' && {
                  isActive: value.IsActive === '1',
                }),
                reportType: value.ReportType || '',
                reportID: value.ReportID,
                ID: value.ID,
                recipientEmails: value.RecipientEmails || '',
                reportAvailable: value.ReportAvailable === '1',
              },
            };
          })}
          columns={combinedColumns}
        />
      </Box>
    </>
  );
};

function Labels({ labels }: { labels: string[] }) {
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.ReportTable' });
  const ref = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (ref.current) {
      setOverflowing(ref.current.scrollHeight > ref.current.clientHeight);
    }
  }, [ref.current, labels]);

  if (!labels.length) return <>-</>;

  return (
    <div className={classes('labelsContainer')}>
      <div ref={ref} className={classes('labels')}>
        {[...labels].map((label, idx) => (
          <Chip
            key={label + idx}
            label={<TransactionTypeLabel transactionType={label} />}
            className={classes('chip')}
          />
        ))}
      </div>

      {overflowing && (
        <Tooltip
          hasButton={false}
          position="left"
          customElement={<div className={classes('viewAll')}>{t('viewAll')}</div>}
          information={
            <div className={classes('labelsTooltip')}>
              <h3>{t('allPaymentRails')}</h3>

              <ul>
                {[...labels].map((label, idx) => (
                  <li key={label + idx}>
                    <Chip label={<TransactionTypeLabel transactionType={label} />} className={classes('label')} />
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      )}
    </div>
  );
}

export default ReportsTable;
